import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import {
  loadUserData, initUserData, saveSection,
  EMPTY_NET_WORTH, EMPTY_BUDGET, EMPTY_GOALS,
  EMPTY_RETIREMENT, EMPTY_CALENDAR, EMPTY_FAMILY,
  EMPTY_USER_PROFILE,
} from '../lib/userData';

const UserDataContext = createContext(null);

export function UserDataProvider({ userId, supabaseUser, children }) {
  const [userData,     setUserData]    = useState(null);
  const [dataLoading,  setDataLoading] = useState(true);
  const [saveError,    setSaveError]   = useState(null);
  const [saveSuccess,  setSaveSuccess] = useState(false);
  const saveSuccessTimer               = useRef(null);

  // ── Load on mount / userId change ──────────────────────────────────────────
  useEffect(() => {
    if (!userId) return;
    setDataLoading(true);

    loadUserData(userId).then(async (row) => {
      if (row === null) {
        // First login — create the row with user's first name from auth metadata
        const firstName = supabaseUser?.user_metadata?.first_name || '';
        const fresh = await initUserData(userId, firstName);
        setUserData(fresh);
      } else {
        setUserData(row);
      }
      setDataLoading(false);
    });
  }, [userId]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Optimistic update ───────────────────────────────────────────────────────
  // Pass options.silent = true to suppress the "Saved" toast (e.g. for theme changes).
  const updateSection = useCallback(async (section, newSectionData, options = {}) => {
    const prev = userData;
    setUserData(d => ({ ...d, [section]: newSectionData }));
    setSaveError(null);
    try {
      await saveSection(userId, section, newSectionData);
      // Show "Saved" confirmation — auto-clear after 2 s (skip for silent saves)
      if (!options.silent) {
        setSaveSuccess(true);
        clearTimeout(saveSuccessTimer.current);
        saveSuccessTimer.current = setTimeout(() => setSaveSuccess(false), 2000);
      }
    } catch (err) {
      setUserData(prev);   // rollback
      setSaveError(`Save failed: ${err.message}`);
    }
  }, [userId, userData]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Derived helpers ─────────────────────────────────────────────────────────
  const nw      = userData?.net_worth    ?? EMPTY_NET_WORTH;
  const budget  = userData?.budget       ?? EMPTY_BUDGET;
  const goals   = userData?.goals        ?? EMPTY_GOALS;
  const retire  = userData?.retirement   ?? EMPTY_RETIREMENT;
  const profile = userData?.user_profile ?? EMPTY_USER_PROFILE;

  const isNewUser = !dataLoading && userData !== null && (
    budget.income === 0 &&
    (nw.assets?.length ?? 0) === 0 &&
    (goals?.length ?? 0) === 0
  );

  const value = {
    userId,                                       // exposed so AIAdvisor can manage chat sessions
    userData,
    dataLoading,
    saveError,
    saveSuccess,                                  // true for ~2 s after each successful save
    connectionError: userData?._error || null,    // non-null when the Supabase table is unreachable
    isNewUser,
    updateSection,

    // Named accessors so components swap import → useUserData() with minimal edits
    netWorthData:   nw,
    budgetData:     budget,
    goalsData:      goals,
    retirementData: retire,
    rsusData:       userData?.rsus      ?? null,
    creditData:     userData?.credit    ?? null,
    calendarData:   userData?.calendar  ?? EMPTY_CALENDAR,
    familyData:     userData?.family    ?? EMPTY_FAMILY,
    investingData:      userData?.investing ?? null,
    userProfile:        profile,
    // Central registry of all user accounts (Plaid-connected + manual)
    linkedAccountsData: profile?.linkedAccounts ?? [],
  };

  return (
    <UserDataContext.Provider value={value}>
      {children}
    </UserDataContext.Provider>
  );
}

export function useUserData() {
  const ctx = useContext(UserDataContext);
  if (!ctx) throw new Error('useUserData must be used within UserDataProvider');
  return ctx;
}
